import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, AfterViewInit, OnDestroy, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { initDatePicker, InitDatePickerResult, isDateBeforeMinDate, preserveLoadedDateTimeWithMinDate } from '../../helpers/date-picker/date-picker.helper';
import 'flatpickr/dist/flatpickr.css';

@Component({
  selector: 'app-flatpickr-date-picker',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './flatpickr-date-picker.component.html',
  styleUrl: './flatpickr-date-picker.component.scss'
})
export class FlatpickrDatePickerComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('dateInput', { static: false }) dateInput!: ElementRef<HTMLInputElement>;
  @ViewChild('inlineContainer', { static: false }) inlineContainer!: ElementRef<HTMLDivElement>;

  @Input() label: string = '';
  @Input() placeholder?: string;
  @Input() disabled: boolean = false;
  @Input() value: string | undefined = '';
  @Input() minDate?: 'today' | string | Date;
  @Input() maxDate?: 'today' | string | Date;
  @Input() dateFormat: string = 'Y/m/d';
  @Input() yearsAhead?: number;
  @Input() yearsBack?: number;
  @Input() startYear?: number;
  @Input() endYear?: number;
  @Input() altFormat?: string;
  @Input() altInput?: boolean = true;
  @Input() required: boolean = false;
  @Input() enableTime: boolean = false;
  @Input() noCalendar: boolean = false;
  @Input() time24hr: boolean = true;
  @Input() allowPastDates: boolean = true;
  @Input() showMonths: number = 1;
  @Input() mode: 'single' | 'range' = 'single';
  @Input() onClose?: (selectedDates: Date[], dateStr: string) => void;
  @Input() inline: boolean = false;

  @Output() valueChange = new EventEmitter<string | undefined>();
  @Output() rangeSelected = new EventEmitter<Date[]>();
  @Output() rangeClear = new EventEmitter<void>();

  computedPlaceholder: string = '';
  private pickers?: InitDatePickerResult;
  private computedYearsBack: number = 0;
  private computedYearsAhead: number = 20;
  private computedMinDate?: 'today' | string | Date;

  ngOnInit(): void {
    this.calculateYearRange();
    if (!this.inline) {
      this.computedPlaceholder = this.placeholder || this.generatePlaceholder();
    }
  }

  ngAfterViewInit(): void {
    // Delay initialization to prevent auto-opening when used inside modals
    setTimeout(() => this.initDatePicker());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value'] && !changes['value'].firstChange && this.pickers?.dateInstance) {
      const newValue = changes['value'].currentValue;
      this.updateDatePickerValue(newValue);
    }
  }

  ngOnDestroy(): void {
    this.pickers?.destroy();
  }

  private calculateYearRange(): void {
    const currentYear = new Date().getFullYear();

    // If yearsBack/yearsAhead are explicitly provided, use them
    if (this.yearsBack !== undefined) {
      this.computedYearsBack = this.yearsBack;
    } else if (this.allowPastDates) {
      // Calculate based on minDate if allowPastDates is true
      if (this.minDate && this.minDate !== 'today') {
        const minDateObj = typeof this.minDate === 'string' ? new Date(this.minDate) : this.minDate;
        if (!isNaN(minDateObj.getTime())) {
          this.computedYearsBack = currentYear - minDateObj.getFullYear();
        }
      } else if (!this.minDate) {
        // Default to 1900 if no minDate specified and allowPastDates is true
        this.computedYearsBack = currentYear - 1900;
        this.computedMinDate = '1900-01-01';
      }
    }

    if (this.yearsAhead !== undefined) {
      this.computedYearsAhead = this.yearsAhead;
    } else if (this.maxDate && this.maxDate !== 'today') {
      const maxDateObj = typeof this.maxDate === 'string' ? new Date(this.maxDate) : this.maxDate;
      if (!isNaN(maxDateObj.getTime())) {
        this.computedYearsAhead = maxDateObj.getFullYear() - currentYear;
      }
    }

    // Ensure non-negative values
    this.computedYearsBack = Math.max(0, this.computedYearsBack);
    this.computedYearsAhead = Math.max(0, this.computedYearsAhead);
  }

  private generatePlaceholder(): string {
    return this.getFlatpickrLocaleFormat({ isPlaceholder: true, timeEnabled: this.enableTime });
  }

  private getFlatpickrLocaleFormat(options: { isPlaceholder?: boolean, timeEnabled?: boolean }): string {
    const locale = navigator.language || 'en-US';
    const sample = new Date(2024, 11, 31);
    const parts = new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(sample);

    const separator = parts.find(p => p.type === 'literal')?.value ?? '/';
    let order: string[] = [];

    if (options.isPlaceholder) {
      order = parts
        .filter(p => p.type === 'day' || p.type === 'month' || p.type === 'year')
        .map(p => (p.type === 'day' ? 'dd' : p.type === 'month' ? 'mm' : 'yyyy'));
    } else {
      order = parts
        .filter(p => p.type === 'day' || p.type === 'month' || p.type === 'year')
        .map(p => (p.type === 'day' ? 'd' : p.type === 'month' ? 'm' : 'Y'));
    }

    const date = order.join(separator);
    return options.timeEnabled ? options.isPlaceholder ? `${date} HH:mm` : `${date} H:i` : date;
  }

  private updateDatePickerValue(newValue: string | undefined): void {
    const fp = this.pickers?.dateInstance;
    if (!fp) return;

    if (!newValue) {
      fp.clear();
      return;
    }

    const effectiveMinDate = this.computedMinDate || this.minDate;

    if (effectiveMinDate && isDateBeforeMinDate(newValue, effectiveMinDate)) {
      preserveLoadedDateTimeWithMinDate(fp, newValue, effectiveMinDate);
      return;
    }

    fp.setDate(newValue, true);
  }

  private initDatePicker(): void {
    // Use computedMinDate if set, otherwise use the provided minDate
    const effectiveMinDate = this.computedMinDate || this.minDate;

    if (this.inline) {
      // Inline mode: render calendar directly without input
      if (!this.inlineContainer?.nativeElement) return;

      this.pickers = initDatePicker({
        inlineContainer: this.inlineContainer.nativeElement,
        inline: true,
        mode: this.mode,
        showMonths: this.showMonths,
        minDate: effectiveMinDate,
        maxDate: this.maxDate,
        dateFormat: this.dateFormat,
        onSave: (selectedDates: Date[]) => {
          this.rangeSelected.emit(selectedDates);
        },
        onClear: () => {
          this.rangeClear.emit();
        }
      });
    } else {
      // Normal mode: input with popup
      const effectiveAltFormat = this.altFormat || this.getFlatpickrLocaleFormat({ timeEnabled: this.enableTime });

      this.pickers = initDatePicker({
        dateInput: this.dateInput,
        getDate: () => this.value || undefined,
        setDate: (v: string) => {
          this.value = v;
          this.valueChange.emit(v);
        },
        minDate: effectiveMinDate,
        maxDate: this.maxDate,
        dateFormat: this.dateFormat,
        altFormat: effectiveAltFormat,
        altInput: this.altInput,
        yearsAhead: this.computedYearsAhead,
        yearsBack: this.computedYearsBack,
        startYear: this.startYear,
        endYear: this.endYear,
        disabled: this.disabled,
        enableTime: this.enableTime,
        noCalendar: this.noCalendar,
        time24hr: this.time24hr,
        showMonths: this.showMonths,
        mode: this.mode,
        onClose: this.onClose
      });
    }
  }
}